import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Collapse,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  TableSortLabel,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  Assignment as EpicIcon,
  ViewList as FeatureIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Assignment as PBIIcon,
  CheckCircle,
  DriveFileMove as MoveToPlanningIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { authGet } from '../../services/apiService';
import { formatDate, formatFixVersion, getQuarter } from '../../utils/formatters';
import { calculateEpicCompletion, calculateFeatureCompletion, calculateWorkItemCompletion, calculateEpicStoryPoints, calculateFeatureStoryPoints } from '../../utils/completionCalculator';
import StatusBadge from '../common/StatusBadge';
import Loading from '../common/Loading';

// Utility functions for sorting
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/** Donut showing completion % with number in center; color by completion (red / amber / green). */
function CompletionDonut({ percentage = 0 }) {
  const value = Math.min(Math.max(Number(percentage), 0), 100);
  const color = value < 33 ? 'error' : value < 67 ? 'warning' : 'success';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        color={color}
        size={40}
        thickness={5}
      />
      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" fontWeight="medium" color="text.primary">
          {Math.round(value)}%
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * WorkItemsTreeView component for displaying work items in a clean tree structure
 * Shows Epic → Feature → Backlog Item hierarchy (tasks are filtered out)
 * @param {Object} props - Component props
 * @param {Array} props.workItems - Array of work items (epics with full hierarchy)
 * @param {Object} props.activeQuarter - Active quarter object
 * @param {Set} props.matchedItemIds - Set of item IDs that matched the search
 * @param {Array} props.quarters - Quarters from timeline config (for Move to Planning), each { year, quarter, label, planId }
 * @param {Function} props.onMoveToPlanning - (adoId, planId) => Promise
 * @param {Set<string>} props.plannedItemIds - ADO IDs that are in Planning (for row background highlight)
 * @returns {JSX.Element} WorkItemsTreeView component
 */
const WorkItemsTreeView = ({ workItems = [], activeQuarter = null, matchedItemIds = new Set(), quarters = [], onMoveToPlanning, plannedItemIds = new Set() }) => {
  const [expandedEpics, setExpandedEpics] = useState({});
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [moveMenuItem, setMoveMenuItem] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [expandedBacklogItems, setExpandedBacklogItems] = useState({});
  const [expandedTestCases, setExpandedTestCases] = useState({});
  const [epicTestCases, setEpicTestCases] = useState({}); // Store test cases per epic ID
  const [loadingBugsTests, setLoadingBugsTests] = useState({}); // Track loading state per epic
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  // Handle sort request
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Helpers: child target date cannot be beyond parent; no date = same timeline as parent
  const isFeatureType = (feature) =>
    !feature.workItemType ||
    feature.workItemType === 'Feature' ||
    feature.level === 'Feature' ||
    (feature.workItemType !== 'Product Backlog Item' &&
      feature.workItemType !== 'Task' &&
      feature.workItemType !== 'PBI' &&
      feature.workItemType !== 'Bug');
  const childAllowedByParent = (childItem, parent) => {
    if (!childItem.targetDate) return true;
    if (!parent || !parent.targetDate) return true;
    return new Date(childItem.targetDate) <= new Date(parent.targetDate);
  };
  const featureInQuarter = (feature, quarterStart, quarterEnd) => {
    if (!feature.targetDate) return false;
    const d = new Date(feature.targetDate);
    return d >= quarterStart && d <= quarterEnd;
  };

  // Filter work items based on quarter
  // When a quarter is selected: only show features (and PBIs) that fall within the quarter boundary and respect parent/child rules.
  const filteredWorkItems = useMemo(() => {
    if (!workItems || workItems.length === 0) return [];
    const quarterStart = activeQuarter?.startDate;
    const quarterEnd = activeQuarter?.endDate;

    return workItems.map(epic => {
      if (activeQuarter) {
        // TBD and To be Planned: show epics with no target date; only show child features that have no target date
        if (activeQuarter.label === 'TBD' || activeQuarter.label === 'To be Planned') {
          if (!epic.targetDate) {
            const featuresNoDate = (epic.features || []).filter(f => !f || !f.targetDate);
            return { ...epic, features: featuresNoDate };
          }
          return null;
        }
        let epicInQuarter = false;
        let epicDateInQuarter = false;
        if (epic.targetDate && quarterStart && quarterEnd) {
          const epicDate = new Date(epic.targetDate);
          if (epicDate >= quarterStart && epicDate <= quarterEnd) {
            epicInQuarter = true;
            epicDateInQuarter = true;
          }
        }
        if (!epicInQuarter && epic.features?.length > 0) {
          const hasFeatureInQuarter = epic.features.some(f => isFeatureType(f) && featureInQuarter(f, quarterStart, quarterEnd));
          if (hasFeatureInQuarter) epicInQuarter = true;
        }
        if (!epicInQuarter) return null;

        // Build features list for this quarter only
        let featuresToShow;
        if (epicDateInQuarter) {
          // Parent (Epic) has date in this quarter: parent date supersedes – show ALL children regardless of their dates
          featuresToShow = (epic.features || []).filter(f => isFeatureType(f) && childAllowedByParent(f, epic));
        } else {
          // Epic has no date (or date in another quarter): only features whose target date is in this quarter
          featuresToShow = (epic.features || []).filter(f => {
            if (!isFeatureType(f)) return false;
            if (!childAllowedByParent(f, epic)) return false;
            return featureInQuarter(f, quarterStart, quarterEnd);
          });
        }
        featuresToShow = featuresToShow.map(feature => ({
          ...feature,
          items: (feature.items || []).filter(item => childAllowedByParent(item, feature))
        }));
        return { ...epic, features: featuresToShow };
      }
      return epic;
    }).filter(epic => epic !== null);
  }, [workItems, activeQuarter]);

  const sortedWorkItems = useMemo(() => {
    return stableSort(filteredWorkItems, getComparator(order, orderBy));
  }, [filteredWorkItems, order, orderBy]);

  // Flat list of rows to render so every <tr> is a direct child of <tbody> (valid HTML, proper alignment)
  const visibleRows = useMemo(() => {
    const rows = [];
    sortedWorkItems.forEach((epic) => {
      rows.push({ type: 'data', item: epic, level: 0 });
      if (!expandedEpics[epic.id]) return;
      (epic.features || []).forEach((feature) => {
        rows.push({ type: 'data', item: feature, level: 1 });
        if (!expandedFeatures[feature.id]) return;
        (feature.items || []).forEach((backlogItem) => {
          if (backlogItem.workItemType === 'Task') return;
          rows.push({ type: 'data', item: backlogItem, level: 2 });
        });
      });
      if (epicTestCases[epic.id]?.length) {
        rows.push({ type: 'tests', epicId: epic.id });
      }
    });
    return rows;
  }, [sortedWorkItems, expandedEpics, expandedFeatures, expandedBacklogItems, epicTestCases]);

  // Auto-expand items that contain matched search results
  useEffect(() => {
    if (matchedItemIds && matchedItemIds.size > 0) {
      // Use functional updates to preserve existing expansions
      setExpandedEpics(prev => {
        const newExpandedEpics = { ...prev };
        sortedWorkItems.forEach(epic => {
          // Check if epic itself matches
          if (matchedItemIds.has(epic.id)) {
            newExpandedEpics[epic.id] = true;
          }
          
          // Check features
          if (epic.features && epic.features.length > 0) {
            epic.features.forEach(feature => {
              // If feature matches, expand epic
              if (matchedItemIds.has(feature.id)) {
                newExpandedEpics[epic.id] = true;
              }
            });
          }
        });
        return newExpandedEpics;
      });
      
      setExpandedFeatures(prev => {
        const newExpandedFeatures = { ...prev };
        sortedWorkItems.forEach(epic => {
          if (epic.features && epic.features.length > 0) {
            epic.features.forEach(feature => {
              // If feature matches, expand feature
              if (matchedItemIds.has(feature.id)) {
                newExpandedFeatures[feature.id] = true;
              }
              
              // Check backlog items
              if (feature.items && feature.items.length > 0) {
                feature.items.forEach(backlogItem => {
                  // If backlog item matches, expand feature
                  if (matchedItemIds.has(backlogItem.id)) {
                    newExpandedFeatures[feature.id] = true;
                  }
                });
              }
            });
          }
        });
        return newExpandedFeatures;
      });
      
      setExpandedBacklogItems(prev => {
        const newExpandedBacklogItems = { ...prev };
        sortedWorkItems.forEach(epic => {
          if (epic.features && epic.features.length > 0) {
            epic.features.forEach(feature => {
              if (feature.items && feature.items.length > 0) {
                feature.items.forEach(backlogItem => {
                  // If backlog item matches, expand backlog item
                  if (matchedItemIds.has(backlogItem.id)) {
                    newExpandedBacklogItems[backlogItem.id] = true;
                  }
                });
              }
            });
          }
        });
        return newExpandedBacklogItems;
      });
    }
  }, [matchedItemIds, sortedWorkItems]); // Only depend on matchedItemIds and sortedWorkItems

  const toggleEpic = (epicId) => {
    setExpandedEpics(prev => ({
      ...prev,
      [epicId]: !prev[epicId]
    }));
    
    // Fetch test cases when epic is expanded (if not already loaded)
    if (!expandedEpics[epicId] && !epicTestCases[epicId] && !loadingBugsTests[epicId]) {
      fetchBugsAndTests(epicId);
    }
  };

  const toggleFeature = (featureId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  const toggleBacklogItem = (itemId) => {
    setExpandedBacklogItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const toggleTestCases = (epicId) => {
    setExpandedTestCases(prev => ({
      ...prev,
      [epicId]: !prev[epicId]
    }));
    
    // Fetch test cases if not already loaded
    if (!epicTestCases[epicId] && !loadingBugsTests[epicId]) {
      fetchBugsAndTests(epicId);
    }
  };

  // Fetch test cases for an epic (bugs are shown inline with PBIs from hierarchy)
  const fetchBugsAndTests = async (epicId) => {
    setLoadingBugsTests(prev => ({ ...prev, [epicId]: true }));
    
    try {
      const response = await authGet(`/api/test-plans/work-item/${epicId}`);
      if (response.success && response.data) {
        setEpicTestCases(prev => ({
          ...prev,
          [epicId]: response.data.testCases || []
        }));
      }
    } catch (error) {
      console.error(`Error fetching test cases for epic ${epicId}:`, error);
    } finally {
      setLoadingBugsTests(prev => ({ ...prev, [epicId]: false }));
    }
  };

  // Get ADO URL
  const getAdoUrl = (workItemId) => {
    return `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${workItemId}`;
  };

  // Render test cases group (collapsed by default)
  const renderTestCasesGroup = (testCases, epicId) => {
    if (!testCases || testCases.length === 0) return null;
    
    const isExpanded = expandedTestCases[epicId] || false;
    
    return (
      <>
        <TableRow 
          hover 
          sx={{ 
            backgroundColor: 'grey.50',
            cursor: 'pointer'
          }}
          onClick={() => toggleTestCases(epicId)}
        >
          <TableCell sx={{ paddingLeft: '28px' }}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleTestCases(epicId); }}>
              {isExpanded ? <ExpandLessIcon /> : <ChevronRightIcon />}
            </IconButton>
          </TableCell>
          <TableCell colSpan={9}>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle sx={{ fontSize: '1.2rem', color: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Tests ({testCases.length})
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={10} sx={{ padding: 0, border: 'none' }}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4 }}>
                {testCases.map(tc => (
                  <TableRow key={`tc-${tc.id}`} hover sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ paddingLeft: '48px' }}>
                      <Box width={24} height={24} />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <CheckCircle sx={{ fontSize: '1.2rem', color: 'success.main' }} />
                        <Typography
                          component="a"
                          href={getAdoUrl(tc.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'success.main',
                            textDecoration: 'none',
                            marginLeft: 1,
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {tc.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{tc.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="Test Case" size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={tc.state} />
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>{tc.team || '-'}</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };


  const getWorkItemIcon = (workItemType) => {
    switch (workItemType) {
      case 'Epic':
        return <EpicIcon fontSize="small" sx={{ color: 'primary.main' }} />;
      case 'Feature':
        return <FeatureIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      case 'Product Backlog Item':
        return <PBIIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 'Bug':
        return <BugIcon fontSize="small" sx={{ color: 'error.main' }} />;
      case 'Task':
        return <TaskIcon fontSize="small" sx={{ color: 'info.main' }} />;
      default:
        return <TaskIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getWorkItemColor = (workItemType) => {
    switch (workItemType) {
      case 'Epic':
        return 'primary.main';
      case 'Feature':
        return 'secondary.main';
      case 'Product Backlog Item':
        return 'info.main';
      case 'Bug':
        return 'error.main';
      case 'Task':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  const getCompletionPercentage = (item) => {
    switch (item.workItemType) {
      case 'Epic':
        return calculateEpicCompletion(item);
      case 'Feature':
        return calculateFeatureCompletion(item);
      case 'Product Backlog Item':
      case 'Bug':
        return calculateWorkItemCompletion(item);
      default:
        return 0;
    }
  };

  const getTotalStoryPoints = (item) => {
    switch (item.workItemType) {
      case 'Epic':
        return calculateEpicStoryPoints(item) || '-';
      case 'Feature':
        return calculateFeatureStoryPoints(item) || '-';
      case 'Product Backlog Item':
      case 'Bug':
        const itemStoryPoints = item.storyPoints || 0;
        const taskStoryPoints = (item.tasks || []).reduce((sum, task) => sum + (task.storyPoints || 0), 0);
        return itemStoryPoints + taskStoryPoints || '-';
      default:
        return item.storyPoints || '-';
    }
  };

  /** Remaining story points = total * (1 - completion/100). Returns '-' when total is non-numeric. */
  const getRemainingStoryPoints = (item) => {
    const total = getTotalStoryPoints(item);
    if (total === '-' || total == null) return '-';
    const totalNum = Number(total);
    if (Number.isNaN(totalNum)) return '-';
    const pct = getCompletionPercentage(item) || 0;
    const remaining = Math.round(totalNum * (1 - pct / 100));
    return remaining;
  };

  /** Count all descendants in the hierarchy (Epic: features + all PBIs under them; Feature: items; PBI/Bug: 0). */
  const getDescendantsCount = (item) => {
    if (item.workItemType === 'Epic') {
      const features = item.features || [];
      const featureCount = features.length;
      const itemsCount = features.reduce((sum, f) => sum + (f.items?.length || 0), 0);
      return featureCount + itemsCount;
    }
    if (item.workItemType === 'Feature') {
      return item.items?.length || 0;
    }
    return 0;
  };

  const renderWorkItemRow = (item, level = 0) => {
    if (item.workItemType === 'Task') return null;

    const isExpanded = level === 0 ? expandedEpics[item.id] :
                      level === 1 ? expandedFeatures[item.id] :
                      expandedBacklogItems[item.id];

    const hasChildren = (item.features && item.features.length > 0) ||
                       (item.items && item.items.length > 0);

    const descendantsCount = getDescendantsCount(item);
    const isMatched = matchedItemIds && matchedItemIds.has(item.id);
    const isPlanned = plannedItemIds && plannedItemIds.has(String(item.id));

    // Indent: level 0 = 8px, level 1 = 32px, level 2 = 56px; first column is 88px so icon fits
    const indentPx = 8 + level * 24;

    return (
      <TableRow
        key={item.id}
        hover
        sx={{
          '&:hover': { backgroundColor: 'action.hover' },
          cursor: 'pointer',
          backgroundColor: isMatched
            ? 'action.selected'
            : isPlanned
              ? (theme) => alpha(theme.palette.primary.main, 0.08)
              : (level > 0 ? 'grey.50' : 'inherit'),
          borderLeft: isMatched ? '3px solid' : 'none',
          borderColor: isMatched ? 'primary.main' : 'transparent'
        }}
        onClick={() => {
          if (level === 0) toggleEpic(item.id);
          else if (level === 1) toggleFeature(item.id);
          else if (level === 2) toggleBacklogItem(item.id);
        }}
      >
        <TableCell sx={{ width: 88, minWidth: 88, paddingLeft: `${indentPx}px`, paddingRight: 0, verticalAlign: 'middle' }}>
          {hasChildren ? (
            <IconButton size="small" sx={{ margin: 0 }}>
              {isExpanded ? <ExpandLessIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : (
            <Box width={24} height={24} sx={{ display: 'inline-block' }} />
          )}
        </TableCell>
        <TableCell sx={{ paddingLeft: `${indentPx}px` }}>
          <Box display="flex" alignItems="center">
            {getWorkItemIcon(item.workItemType)}
            <Typography
              component="a"
              href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: getWorkItemColor(item.workItemType),
                textDecoration: 'none',
                marginLeft: 1,
                '&:hover': { textDecoration: 'underline', color: getWorkItemColor(item.workItemType) }
              }}
            >
              {item.id}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ paddingLeft: `${indentPx}px`, maxWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={level === 0 ? 'medium' : 'normal'}
            sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
          >
            {item.title}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={item.workItemType || 'Unknown'}
            size="small"
            color={
              item.workItemType === 'Epic' ? 'primary' :
              item.workItemType === 'Feature' ? 'secondary' :
              item.workItemType === 'Product Backlog Item' ? 'success' :
              item.workItemType === 'Bug' ? 'error' :
              item.workItemType === 'Task' ? 'info' : 'default'
            }
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <StatusBadge status={item.state} />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {item.targetDate ? formatDate(item.targetDate) : '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{item.team || '-'}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{getTotalStoryPoints(item)}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{getRemainingStoryPoints(item)}</Typography>
        </TableCell>
        <TableCell>
          <CompletionDonut percentage={getCompletionPercentage(item)} />
        </TableCell>
        <TableCell>
          <Chip label={descendantsCount} size="small" color="primary" variant="outlined" />
        </TableCell>
      </TableRow>
    );
  };

  if (!workItems || workItems.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="body1" color="textSecondary">
          No work items found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100, tableLayout: 'fixed', '& .MuiTableCell-root': { py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 88, minWidth: 88, py: 0.75 }}></TableCell>
              <TableCell sx={{ width: 88, minWidth: 88 }}>ID</TableCell>
              <TableCell sx={{ minWidth: 420, width: '40%' }}>Title</TableCell>
              <TableCell sx={{ width: 110, minWidth: 110 }}>Type</TableCell>
              <TableCell 
                sx={{ width: 100, minWidth: 100, cursor: 'pointer' }}
                sortDirection={orderBy === 'state' ? order : false}
                onClick={(event) => handleRequestSort(event, 'state')}
              >
                <TableSortLabel
                  active={orderBy === 'state'}
                  direction={orderBy === 'state' ? order : 'asc'}
                >
                  State
                </TableSortLabel>
              </TableCell>
              <TableCell 
                sx={{ width: 110, minWidth: 110, cursor: 'pointer' }}
                sortDirection={orderBy === 'targetDate' ? order : false}
                onClick={(event) => handleRequestSort(event, 'targetDate')}
              >
                <TableSortLabel
                  active={orderBy === 'targetDate'}
                  direction={orderBy === 'targetDate' ? order : 'asc'}
                >
                  Target Date
                </TableSortLabel>
              </TableCell>
              <TableCell 
                sx={{ width: 100, minWidth: 100, cursor: 'pointer' }}
                sortDirection={orderBy === 'team' ? order : false}
                onClick={(event) => handleRequestSort(event, 'team')}
              >
                <TableSortLabel
                  active={orderBy === 'team'}
                  direction={orderBy === 'team' ? order : 'asc'}
                >
                  Team
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 90, minWidth: 90 }}>Story Points</TableCell>
              <TableCell sx={{ width: 100, minWidth: 100 }}>Remaining SP</TableCell>
              <TableCell sx={{ width: 100, minWidth: 100 }}>Completion</TableCell>
              <TableCell sx={{ width: 90, minWidth: 90 }}>Descendants</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <React.Fragment key={row.type === 'data' ? row.item.id : `tests-${row.epicId}`}>
                {row.type === 'data'
                  ? renderWorkItemRow(row.item, row.level)
                  : renderTestCasesGroup(epicTestCases[row.epicId] || [], row.epicId)}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedWorkItems.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No work items match the selected filters.
          </Typography>
        </Box>
      )}

      {sortedWorkItems.length > 0 && (
        <Box sx={{ mt: 2, p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {sortedWorkItems.length} of {workItems.length} epics
          </Typography>
        </Box>
      )}

      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => { setMoveMenuAnchor(null); setMoveMenuItem(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {quarters
          .filter((q) => q.year != null && q.quarter != null)
          .map((q) => {
            const planId = `${q.year}Q${q.quarter}`;
            return (
              <MenuItem
                key={planId}
                onClick={async () => {
                  if (!moveMenuItem || !onMoveToPlanning) return;
                  const adoId = moveMenuItem.id;
                  setMoveMenuAnchor(null);
                  setMovingId(adoId);
                  try {
                    await onMoveToPlanning(adoId, planId);
                  } finally {
                    setMovingId(null);
                    setMoveMenuItem(null);
                  }
                }}
              >
                {q.label}
              </MenuItem>
            );
          })}
      </Menu>
    </Paper>
  );
};

export default WorkItemsTreeView;
